import { PreLoginKcRealmVO } from '@repo/types'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { discoverKeycloakRealm, KC_INFO, store, UPDATE_USER } from '@web/modules/core/utils'
import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { STAGES } from '../utils'
import { PreLoginFormValues } from './usePreLoginForm'

interface UseStageMailProps {
  realmParam?: string | null
  stage?: string
  setStage?: (stage: string) => void
  setOptions?: (options: PreLoginKcRealmVO[]) => void
}

const RECENT_EMAIL_KEY = 'recent_email'

export const useStageEmail = ({ setStage, setOptions, realmParam }: UseStageMailProps) => {
  const form = useFormContext<PreLoginFormValues>()
  const { getValues: getFormValue, trigger: triggerValidationOn } = form
  const { snackbar } = useSnackbar()
  const [loading, setLoading] = useState<boolean>(false)

  const emailFormKey = 'mail'

  useEffect(() => {
    const email = localStorage.getItem(RECENT_EMAIL_KEY)

    form.setValue('mail', email ?? '')
  }, [form])

  const updateRecentEmailAddress = useCallback((email: string) => {
    localStorage.setItem(RECENT_EMAIL_KEY, email)
  }, [])

  const discoveryRealmAndRedirect = async () => {
    try {
      const isValid = await triggerValidationOn(emailFormKey)
      if (!isValid) {
        return
      }

      setLoading(true)
      const email = getStandardEmail()
      const availableRealms = await discoverKeycloakRealm(email)

      if (availableRealms.length === 0) {
        snackbar.show('error', 'Usuário não encontrado')
        setLoading(false)
        return
      }

      updateRecentEmailAddress(email)

      if (availableRealms.length === 1) {
        redirect(availableRealms[0])
        return
      }

      if (realmParam && availableRealms.length > 1) {
        const realmByParam = availableRealms.find(it => it.realm === realmParam)

        if (realmByParam) {
          redirect(realmByParam)
          return
        }
      }

      setOptions?.(availableRealms.sort((a, b) => a.realm.localeCompare(b.realm)))

      setStage?.(STAGES.REALM)
    } catch (e) {
      snackbar.show('error', 'Ooops, algo deu errado! tente novamente')
    } finally {
      setLoading(false)
    }
  }

  const getStandardEmail = () => {
    const mail = getFormValue(emailFormKey)
    return mail.toLocaleLowerCase()
  }

  const redirect = (realm: PreLoginKcRealmVO) => {
    store(KC_INFO, realm)
    store(UPDATE_USER, true)

    window.location.assign(realm.loginUrl)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      discoveryRealmAndRedirect().then()
    }
  }

  return {
    form,
    loading,
    discoveryRealmAndRedirect,
    onKeyDown
  }
}
